import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import { SectionCard, Divider } from "@/components/ds";
import { Lightbulb } from "@/components/ds/icons";
import IconByName from "@/components/ds/IconByName";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationStickyHeader, { IntegrationTab } from "../shared/IntegrationStickyHeader";

const CB_COLOR = "#C62828";

// ─── Exported nav helpers ──────────────────────────────────────────────────────

export function BackToIntegrationLandingCloudbeds() {
  const navigate = useNavigate();
  return (
    <button type="button" className="flex cursor-pointer items-center gap-2 text-zinc-600"
      onClick={() => navigate("/integrations")}>
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
      </svg>
      Back to Integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CloudbedsConfig {
  id: number;
  siteId: number;
  displayName: string;
  propertyId: string;
  apiKeyMasked: string;
  enabled: boolean;
  isActive?: boolean;
  propertyName?: string;
  createdAt: string;
  pushTranscripts?: boolean;
}

type TabId = "overview" | "accounts" | "ai-tools" | "field-reference" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Reservation management",
    icon: "Building",
    items: [
      "Look up any reservation by ID or guest name — room type, dates, rate plan, folio balance, and status",
      "Search reservations by guest email or arrival date — full history across all room types",
      "Create, modify, and cancel reservations from chat — with cancellation policy check before every cancel",
      "cloudbeds_get_reservation / cloudbeds_search_reservations — AI looks up stays automatically based on guest questions"
    ]
  },
  {
    title: "Guest services & folio",
    icon: "DollarSign",
    items: [
      "Guest profile lookup by email — stay history, loyalty tier, and past folio summary",
      "cloudbeds_get_folio: Itemized bill breakdown — room charges, F&B, extras, taxes, and balance due",
      "cloudbeds_process_payment: Post a payment to the guest folio during chat — supports card, cash, and credit",
      "cloudbeds_add_charge: Add incidental charges (minibar, parking, late checkout) directly to the folio from chat"
    ]
  },
  {
    title: "Availability & room types",
    icon: "Package",
    items: [
      "Real-time room availability check for any date range — returns available room types with rates",
      "Room type details — bed configuration, view, amenities, max occupancy, and photos URL",
      "Rate plans available for dates — best available, early bird, non-refundable, and package rates",
      "cloudbeds_get_packages: Add-on packages available for a stay — breakfast, spa, romance, and more"
    ]
  },
  {
    title: "Housekeeping & operations",
    icon: "Zap",
    items: [
      "cloudbeds_get_housekeeping_status: Real-time room cleaning status — Clean, Dirty, Inspected, Do Not Disturb",
      "cloudbeds_send_confirmation: Resend booking confirmation email instantly from chat — no front desk needed",
      "Property info — address, check-in/out times, amenities, contact numbers, and parking details",
      "Cancellation and pet policies — answer policy questions without transferring to the front desk"
    ]
  },
  {
    title: "AI workflow tools",
    icon: "Zap",
    items: [
      "20 tools available: get_reservation, search_reservations, get_guest, check_availability, get_room_type, get_pricing, get_folio, create_reservation, update_reservation, cancel_reservation, get_housekeeping_status, process_payment, get_amenities, get_policies, get_packages, add_charge, get_guest_history, send_confirmation, get_rate_plans, get_property_info",
      "No workflow configuration required — AI calls Cloudbeds tools based on what the guest asks",
      "Multiple properties supported — connect hotel groups or chains under one Velaro site",
      "Field mapping is optional — AI works immediately; mapping adds agent sidebar and HTTP POST variables"
    ]
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "cloudbeds_get_reservation", label: "Get reservation", group: "Reservations", description: "Full reservation by ID — guest, room type, dates, rate plan, folio total, and status." },
  { name: "cloudbeds_search_reservations", label: "Search reservations", group: "Reservations", description: "Find reservations by guest name, email, room number, or arrival date." },
  { name: "cloudbeds_create_reservation", label: "Create reservation", group: "Reservations", description: "Book a new reservation for a guest — room type, dates, rate plan, and guest info." },
  { name: "cloudbeds_update_reservation", label: "Update reservation", group: "Reservations", description: "Modify an existing reservation — change dates, room type, or guest count." },
  { name: "cloudbeds_cancel_reservation", label: "Cancel reservation", group: "Reservations", description: "Cancel with cancellation policy check — returns cancellation fee and refund amount." },
  { name: "cloudbeds_send_confirmation", label: "Resend confirmation", group: "Reservations", description: "Resend the booking confirmation email to the guest's email address from chat." },
  { name: "cloudbeds_get_guest", label: "Get guest profile", group: "Guests", description: "Guest details: name, email, phone, loyalty tier, and previous stay summary." },
  { name: "cloudbeds_get_guest_history", label: "Guest stay history", group: "Guests", description: "All past reservations for a guest — dates, rooms, rates, and folio totals." },
  { name: "cloudbeds_check_availability", label: "Check availability", group: "Availability", description: "Real-time room availability for date range — returns available room types with base rates." },
  { name: "cloudbeds_get_room_type", label: "Get room type", group: "Availability", description: "Room type details: bed configuration, view, amenities, max occupancy, and size." },
  { name: "cloudbeds_get_rate_plans", label: "Get rate plans", group: "Availability", description: "Available rate plans for a date range — BAR, early bird, non-refundable, and package rates." },
  { name: "cloudbeds_get_pricing", label: "Get pricing", group: "Availability", description: "Nightly rate, taxes, and total for a room type on specific dates with selected rate plan." },
  { name: "cloudbeds_get_packages", label: "Get packages", group: "Availability", description: "Add-on packages available — breakfast, spa, romance, parking, and seasonal bundles." },
  { name: "cloudbeds_get_folio", label: "Get guest folio", group: "Billing", description: "Itemized guest bill — room charges, F&B, incidentals, taxes, payments, and balance due." },
  { name: "cloudbeds_process_payment", label: "Process payment", group: "Billing", description: "Post a payment to the guest folio — card, cash, or house account credit." },
  { name: "cloudbeds_add_charge", label: "Add folio charge", group: "Billing", description: "Post an incidental charge to a guest folio — minibar, parking, late checkout, or other." },
  { name: "cloudbeds_get_housekeeping_status", label: "Housekeeping status", group: "Operations", description: "Room cleaning status — Clean, Dirty, Inspected, or Do Not Disturb for any room number." },
  { name: "cloudbeds_get_amenities", label: "Get property amenities", group: "Property", description: "Full amenities list — pool, gym, spa, restaurant, parking, and pet policy." },
  { name: "cloudbeds_get_policies", label: "Get policies", group: "Property", description: "Cancellation, check-in/out, pet, smoking, and payment policies." },
  { name: "cloudbeds_get_property_info", label: "Get property info", group: "Property", description: "Property details — address, phone, check-in/out times, parking instructions, and local tips." }
];

// ─── Field Reference ──────────────────────────────────────────────────────────

const CB_FIELD_REFERENCE = [
  {
    scope: "Reservation",
    scopeKey: "reservation",
    fields: [
      { field: "id", label: "Reservation ID", notes: "Cloudbeds internal reservation ID" },
      { field: "reservation_id", label: "Reservation Number", notes: "Guest-facing confirmation number" },
      { field: "status", label: "Status", notes: "confirmed / checked_in / checked_out / cancelled / no_show" },
      { field: "check_in_date", label: "Check-In Date", notes: "YYYY-MM-DD" },
      { field: "check_out_date", label: "Check-Out Date", notes: "YYYY-MM-DD" },
      { field: "nights", label: "Nights", notes: "Duration of stay" },
      { field: "adults", label: "Adults", notes: "Number of adult guests" },
      { field: "children", label: "Children", notes: "Number of child guests" },
      { field: "room_type_name", label: "Room Type", notes: "Name of booked room category" },
      { field: "rate_plan_name", label: "Rate Plan", notes: "BAR / Early Bird / Non-Refundable / etc." },
      { field: "total_due", label: "Total Due", notes: "Outstanding balance on folio" },
      { field: "grand_total", label: "Grand Total", notes: "Total reservation value including taxes" }
    ]
  },
  {
    scope: "Guest",
    scopeKey: "guest",
    fields: [
      { field: "guest_id", label: "Guest ID", notes: "Cloudbeds internal guest profile ID" },
      { field: "first_name", label: "First Name", notes: "" },
      { field: "last_name", label: "Last Name", notes: "" },
      { field: "email", label: "Email", notes: "Primary contact email" },
      { field: "phone", label: "Phone", notes: "Mobile or primary phone" },
      { field: "country", label: "Country", notes: "ISO country code" },
      { field: "loyalty_tier", label: "Loyalty Tier", notes: "Standard / Silver / Gold / Platinum (if program enabled)" },
      { field: "total_stays", label: "Total Stays", notes: "Lifetime stay count at this property" }
    ]
  },
  {
    scope: "Folio",
    scopeKey: "folio",
    fields: [
      { field: "balance", label: "Balance Due", notes: "Current outstanding balance" },
      { field: "total_charges", label: "Total Charges", notes: "Gross charges before payments" },
      { field: "total_payments", label: "Total Payments", notes: "Sum of all payments applied" },
      { field: "room_charges", label: "Room Charges", notes: "Nightly room rate total" },
      { field: "tax_total", label: "Taxes", notes: "Total tax amount on the folio" },
      { field: "currency", label: "Currency", notes: "3-letter ISO currency code" }
    ]
  }
];

// ─── Shared UI Components ─────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">{children}</pre>;
}

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(`{${variable}}`); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ok */ }
  }
  return (
    <button type="button" onClick={copy} title={`Copy {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded border font-mono transition-colors ${copied ? "border-green-300 bg-green-50 text-green-700" : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"}`}
      aria-label={`Copy variable {${variable}}`}>
      {copied ? "✓" : `{${variable}}`}
    </button>
  );
}

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));
  function toggleSection(i: number) {
    setOpenSections((prev) => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next; });
  }
  return (
    <SectionCard className="mb-4">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl" aria-expanded={open}>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-800">What's included</span>
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">{CAPABILITIES.length} feature areas</span>
        </div>
        <svg className={`w-4 h-4 text-zinc-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>
      {open && (
        <>
        <Divider className="my-0" /><div className="px-6 pb-6">
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-xs text-green-800">
            <strong>AI tools work without any configuration.</strong> Connect Cloudbeds (API key + Property ID) and the AI gains live access to reservations, room types, availability, folios, and housekeeping status.
          </div>
          <div className="space-y-2">
            {CAPABILITIES.map((cap, i) => (
              <div key={i} className="rounded-lg border border-zinc-100 overflow-hidden">
                <button type="button" onClick={() => toggleSection(i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50" aria-expanded={openSections.has(i)}>
                  <div className="flex items-center gap-3">
                    <IconByName name={cap.icon} className="w-4 h-4" />
                    <span className="text-sm font-medium text-zinc-700">{cap.title}</span>
                  </div>
                  <svg className={`w-3.5 h-3.5 text-zinc-400 transition-transform flex-shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>
                {openSections.has(i) && (
                  <div className="px-4 pb-3 space-y-1.5 border-t border-zinc-50">
                    {cap.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 pt-1.5">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 bg-green-500" />
                        <span className="text-xs text-zinc-600">{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        </>
      )}
    </SectionCard>
  );
}

function HowItWorksCard() {
  return (
    <SectionCard title="How Cloudbeds works in Velaro" description="Hotel-grade AI guest services — from booking questions to folio charges, all in chat." className="mb-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl bg-zinc-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ backgroundColor: CB_COLOR }}>1</span>
            <span className="font-semibold text-zinc-700 text-sm">Connect your property</span>
          </div>
          <p className="text-xs text-zinc-500 mb-3">Enter your Cloudbeds API key and Property ID — all reservation data becomes instantly AI-searchable.</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">API Key</span>
              <span className="text-zinc-400">from Apps & Integrations → API</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <span className="font-mono bg-white border border-zinc-200 px-1.5 py-0.5 rounded text-[10px]">Property ID</span>
              <span className="text-zinc-400">from your Cloudbeds dashboard URL</span>
            </div>
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
            <span className="font-semibold text-green-800 text-sm">20 AI tools activate</span>
          </div>
          <p className="text-xs text-green-700 mb-3">Reservations, folios, housekeeping, and policies — all answerable in real time.</p>
          <div className="grid grid-cols-2 gap-1">
            {["Reservations", "Guest folios", "Room availability", "Rate plans", "Housekeeping", "Packages", "Payments", "Cancellations", "Guest history", "Policies"].map((t) => (
              <div key={t} className="flex items-center gap-1 text-[10px] text-green-700">
                <svg viewBox="0 0 8 8" width="8" height="8" fill="none" className="flex-shrink-0"><path d="M1 4L3 6L7 2" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" /></svg>
                {t}
              </div>
            ))}
          </div>
          <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
            <svg viewBox="0 0 10 10" width="10" height="10" fill="none"><path d="M3 2L7 5L3 8" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-[var(--color-primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
            <span className="font-semibold text-blue-800 text-sm">Full hotel AI services</span>
          </div>
          <p className="text-xs text-blue-700 mb-3">Guests get hotel-grade service in chat — without calling the front desk.</p>
          <div className="space-y-2">
            {[
              ["Pre-arrival bot", "Check-in time, parking, amenities, and local tips"],
              ["In-stay concierge", "Folio questions, room service, housekeeping requests"],
              ["Booking bot", "Check availability, quote rates, and book directly"]
            ].map(([title, desc]) => (
              <div key={title} className="bg-blue-100/40 rounded-lg p-2">
                <div className="text-[10px] font-semibold text-blue-800 uppercase tracking-wide">{title}</div>
                <div className="text-[10px] text-blue-600 mt-0.5">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: { hasConnection: boolean; onConnect(): void; onOpenWizard(): void }) {
  return (
    <div>
      <CapabilitiesPanel />
      <HowItWorksCard />
      {!hasConnection ? (
        <SectionCard className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-zinc-800 mb-1">Connect your Cloudbeds property</div>
              <p className="text-sm text-zinc-500">Paste your API key and Property ID to enable AI-powered guest services across your hotel, resort, or hostel.</p>
            </div>
            <button type="button" onClick={onConnect} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: CB_COLOR }}>
              Connect Now →
            </button>
          </div>
        </SectionCard>
      ) : (
        <SectionCard className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-zinc-800 mb-1">Launch Starter Workflow Wizard</div>
              <p className="text-sm text-zinc-500">Build a Pre-Arrival Bot, In-Stay Concierge, or Booking Bot in minutes using a pre-built Cloudbeds template.</p>
            </div>
            <button type="button" onClick={onOpenWizard} className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: CB_COLOR }}>
              Launch Wizard →
            </button>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-zinc-800 text-lg flex items-center gap-2">
            AI Tools
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
              {hasConnection ? `${AI_TOOLS.length} active` : "No connection"}
            </span>
          </div>
          <p className="text-sm text-zinc-500 mt-1">All tools activate automatically on connect — no configuration required.</p>
        </div>
        {hasConnection && (
          <button type="button" onClick={onOpenWizard} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: CB_COLOR }}>
            Launch Starter Wizard →
          </button>
        )}
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div key={tool.name} className="rounded-lg bg-zinc-50 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: CB_COLOR }} />
                    <div>
                      <div className="text-xs font-semibold text-zinc-700">{tool.label}</div>
                      <div className="text-[10px] font-mono text-zinc-400 mb-1">{tool.name}</div>
                      <div className="text-xs text-zinc-500">{tool.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function AccountsTab({ configs, onRefresh, onAdd }: { configs: CloudbedsConfig[]; onRefresh(): void; onAdd(): void }) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  async function handleDelete(id: number) {
    if (!confirm("Remove this Cloudbeds connection?")) return;
    setDeleting(id);
    try { await api.admin.delete(`Cloudbeds/Configs/${id}`); onRefresh(); } catch { /* graceful */ } finally { setDeleting(null); }
  }
  return (
    <SectionCard title="Connected Properties">
      <div className="flex items-center justify-between mb-4">
        <div></div>
        <button type="button" onClick={onAdd} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: CB_COLOR }}>
          + Add Property
        </button>
      </div>
      {configs.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <div className="flex justify-center mb-3"><IconByName name="Building" className="w-10 h-10 text-zinc-400" /></div>
          <div className="font-medium text-zinc-600 mb-1">No Cloudbeds property connected</div>
          <p className="text-sm">Connect your API key and property ID to enable AI-powered hotel guest services.</p>
          <button type="button" onClick={onAdd} className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: CB_COLOR }}>
            Connect Cloudbeds →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map((c) => (
            <div key={c.id} className="rounded-xl border border-zinc-200 p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: CB_COLOR }}>C</div>
                <div>
                  <div className="font-medium text-zinc-800">{c.displayName}</div>
                  <div className="text-xs text-zinc-500">{c.propertyName || c.propertyId}</div>
                  <div className="text-xs text-zinc-400 font-mono">API key: {c.apiKeyMasked}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {c.enabled ? "Active" : "Disabled"}
                </span>
                <button type="button" onClick={() => handleDelete(c.id)} disabled={deleting === c.id} className="text-xs text-red-500 hover:text-red-700 transition-colors">
                  {deleting === c.id ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function TranscriptAutoPushCard({
  configs,
  onToggle,
  togglingId,
  error,
}: {
  configs: CloudbedsConfig[];
  onToggle(config: CloudbedsConfig): void;
  togglingId: number | null;
  error: string | null;
}) {
  if (configs.length === 0) return null;
  return (
    <SectionCard title="Transcript Auto-Push" className="mb-4">
      <div className="text-sm text-zinc-500 mb-4">
        When enabled, the full chat transcript is automatically attached as a note on the matched Cloudbeds guest reservation after every conversation ends.
      </div>
      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700" role="alert">
          Failed to update transcript push: {error}
        </div>
      )}
      <div className="space-y-3">
        {configs.map((config) => (
          <div key={config.id} className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0">
            <div>
              <div className="text-sm font-medium text-zinc-700">{config.displayName || config.propertyName || config.propertyId}</div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {config.pushTranscripts
                  ? "Transcript attached to the matched reservation after each conversation"
                  : "Transcript push is off for this property"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onToggle(config)}
              disabled={togglingId === config.id}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 disabled:opacity-50 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
              aria-label={config.pushTranscripts ? `Disable transcript push for ${config.displayName || config.propertyId}` : `Enable transcript push for ${config.displayName || config.propertyId}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
        ))}
      </div>
      {configs.some((c) => c.pushTranscripts && c.enabled) && (
        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
          Auto-push active — transcripts will appear as notes on the matched Cloudbeds reservation after each conversation.
        </div>
      )}
    </SectionCard>
  );
}

function FieldReferenceTab() {
  const [activeScope, setActiveScope] = useState("Reservation");
  const scopeData = CB_FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? CB_FIELD_REFERENCE[0];
  return (
    <SectionCard title="Field Reference" description="Variable names for HTTP POST nodes — push Cloudbeds data to your CRM, PMS, or revenue management system.">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4 text-xs text-blue-800">
        <div className="font-semibold mb-2">Example HTTP POST variables</div>
        <CodeBlock>{`{
  "guest_name":     "{cloudbeds.guest.first_name} {cloudbeds.guest.last_name}",
  "guest_email":    "{cloudbeds.guest.email}",
  "reservation_id": "{cloudbeds.reservation.reservation_id}",
  "check_in":       "{cloudbeds.reservation.check_in_date}",
  "check_out":      "{cloudbeds.reservation.check_out_date}",
  "room_type":      "{cloudbeds.reservation.room_type_name}",
  "balance_due":    "{cloudbeds.folio.balance}"
}`}</CodeBlock>
      </div>
      <div className="flex gap-2 mb-4">
        {CB_FIELD_REFERENCE.map((s) => (
          <button key={s.scope} type="button" onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeScope === s.scope ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            style={activeScope === s.scope ? { backgroundColor: CB_COLOR } : {}}>
            {s.scope}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div className="px-4 py-2.5 text-white text-xs font-semibold" style={{ backgroundColor: CB_COLOR }}>{scopeData.scope} fields</div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Field</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Label</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Variable</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">{f.field}</td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2"><CopyVariableButton variable={`cloudbeds.${scopeData.scopeKey}.${f.field}`} /></td>
                  <td className="px-3 py-2 text-zinc-400 italic">{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Cloudbeds() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<CloudbedsConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Cloudbeds/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  async function togglePushTranscripts(config: CloudbedsConfig) {
    setToggleError(null);
    setTogglingId(config.id);
    try {
      const resp = await api.admin.put(`Cloudbeds/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts,
      });
      if (!resp.ok) {
        let body = "";
        try { body = await resp.text(); } catch { /* body unavailable */ }
        throw new Error(`${resp.status}${body ? ` - ${body}` : ""}`);
      }
      const updated = await resp.json();
      setConfigs((prev) => prev.map((c) => (c.id === config.id ? { ...c, pushTranscripts: updated.pushTranscripts } : c)));
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : String(err));
    } finally {
      setTogglingId(null);
    }
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "accounts", label: "Properties" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "field-reference", label: "Field Reference" },
    { id: "logs", label: "Activity Log" }
  ];

  const tabsJSX = (
    <>
      {TABS.map((tab) => (
        <IntegrationTab key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
          {tab.label}
          {tab.id === "accounts" && configs.length > 0 && (
            <span className="ml-1.5 text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>
          )}
        </IntegrationTab>
      ))}
    </>
  );

  return (
    <IntegrationStickyHeader title="Cloudbeds" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard integration="cloudbeds" onClose={() => setShowWizard(false)} />
      )}

      {hasActiveConfig && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Cloudbeds integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition">Chat Window</Link>
              <Link to="/deployment" className="text-xs px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:opacity-90 text-white transition">Get Embed Code →</Link>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <Link to="/deployment" className="underline hover:text-blue-900">Deployments → Page Widgets</Link> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab hasConnection={hasActiveConfig}
              onConnect={() => navigate("/integrations/cloudbeds/create")}
              onOpenWizard={() => setShowWizard(true)} />
          )}
          {activeTab === "accounts" && (
            <>
              <AccountsTab configs={configs} onRefresh={fetchConfigs} onAdd={() => navigate("/integrations/cloudbeds/create")} />
              <TranscriptAutoPushCard configs={configs} onToggle={togglePushTranscripts} togglingId={togglingId} error={toggleError} />
            </>
          )}
          {activeTab === "ai-tools" && <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />}
          {activeTab === "field-reference" && <FieldReferenceTab />}
          {activeTab === "logs" && (
            <SectionCard title="Activity Log">
              <IntegrationActivityLog integrationSlug="Cloudbeds" />
            </SectionCard>
          )}
        </>
      )}
    </IntegrationStickyHeader>
  );
}
